/* eslint-disable no-restricted-globals */
import React from 'react';
import VoucherTable from './components/VoucherTable';
import VoucherEditForm from './components/edit/VoucherEditForm';

import { useState, useEffect, useRef } from 'react';
import PageTitle from '../../common/components/PageTitle';
import { get, getAll, remove, searchTags, createOrUpdate } from './services/VoucherService';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import useThrowAsyncError from '../../common/hooks/useThrowAsyncError';
import { useTranslation, withTranslation } from 'react-i18next';
import { useFlashMessages } from 'common/contexts/FlashMessages';
import TablePagination from 'common/components/TablePagination';
import { useAuthentication } from 'common/contexts/Authentication';
import { useLoadingIndication } from 'common/hooks/useLoadingIndication';
import LoadingIndicator from 'common/components/LoadingIndicator';

const config = window.voucherManagementConfig.ui;

const empty = () =>
({
  name: {
    fi: '',
  },
  description: {
    fi: '',
  },
  publiclyAvailable: true,
  tags: [],
  discount: {
    type: config.discountTypes.defaultOptionKey,
    value: 0,
  },
  useType: config.defaultVoucherUseTypeKey,
  validity: {
    validSince: null,
    validUntil: null,
    maxUseCount: 0,
  },
  rule: {
    key: '',
    definition: {
      key: window.voucherManagementConfig.api.stampsUseTypeSettings.businessRuleKey,
    },
    expression: [],
    parameterValues: {}
  },
});


const VoucherManagement = () => {

  let { path } = useRouteMatch();
  const throwAsyncError = useThrowAsyncError();
  const { flashMessages, addFlashMessage } = useFlashMessages();
  const authentication = useAuthentication();
  const { t, i18n } = useTranslation('voucherManagement');
  const [startLoading, finishLoading, renderIndicatorIfLoading] = useLoadingIndication(<LoadingIndicator />);

  const history = useHistory();
  const widthRef = useRef();

  const [model, setModel] = useState(empty());

  const discountTypes = config.discountTypes.options(t);

  const [ width, setWidth ] = useState(0);
  const [ first, setFirst ] = useState(true);
  const [ search, setSearch ] = useState('');
  const [ currentData, setCurrentData ] = useState([]);
  const [saved, setSaved] = useState(null);
  const [item, setItem] = useState({});
  const [voucherTagSuggestions, setVoucherTagSuggestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [result, setResult] = useState({
    items: [],
    page: 0,
    totalPageCount: 0,
  });

  const gotoEdit = async (item) => {

    if (item) {
      const existing = await get(authentication.getMerchantUuid(), item.uuid);
      setModel({ ...existing, ...{ rule: (existing.rule || empty().rule) } });
    } else {
      setModel(empty());
    }

    const key = item && item.uuid ? item.key : 'new';
    history.push(`${path}/${key}`);
  };

  const gotoList = () => {
    clearItem();
    history.push(`${path}`);
  };

  const clearItem = () => {
    setItem(empty());
  };

  const handleChange = (name, value) => {
    let update = { ...model };


    if (!Array.isArray(value) && typeof value === 'object') {
      if (name) {
        update[name] = { ...update[name], ...value };
      } else {
        update = { ...update, ...value };
      }
    } else {
      update[name] = value;
    }

    setModel(update);
  };

  const handleSubmit = async () => {
    try {
      const newModel = model;
      newModel.description.fi = model.description.fi.replace(/target="mobile"/g, 'data-target="mobile"');
      await createOrUpdate(authentication.getMerchantUuid(), { ...model, definition: undefined });
      addFlashMessage({ variant: 'success', text: 'common:messages.save.succeeded' });
      setSaved(model);
      gotoList();
    } catch (e) {
      addFlashMessage({ variant: 'danger', text: 'common:messages.save.failed' });
    }
  };

  useEffect(() => {
    console.info('Get vouchers');

    startLoading();
    getAll(authentication.getMerchantUuid(), currentPage > 0 ? currentPage - 1 : currentPage).then((result) => {
      setResult(result);
      finishLoading();
    }).catch(e => throwAsyncError(e));
    finishLoading();

  }, [saved, currentPage]);

  useEffect(() => {
    setCurrentData(result.items);
  }, [ result ]);

  useEffect(() => {
    searchTags(model.tags, '').then(result => setVoucherTagSuggestions(result.map(t => ({ id: t, text: t }))));
  }, [ model.tags ]);

  function autocompleteMatch(input) {
    if (input === '') {
      setCurrentData(result.items);
    };
    const reg = new RegExp(input.toLowerCase());
    setCurrentData(result.items.filter((term) => {
      if (term.name.fi.toLowerCase().match(reg)) {
        return term;
      }
      return null;
    }));
  };

  useEffect(() => {
    autocompleteMatch(search);
  }, [ search ]);

  useEffect(() => {
    if(widthRef.current && first){
      setWidth(widthRef.current.offsetWidth);
    }
  }, [ widthRef ]);

  const handleRemove = async (item) => {
    // eslint-disable-next-line no-alert
    // eslint-disable-next-line no-restricted-globals
    // eslint-disable-next-line no-alert
    if (confirm(t('messages.confirm.removal', { name: item.name.fi }))) {
      await remove(authentication.getMerchantUuid(), item);
      setSaved(item);
      addFlashMessage({ variant: 'success', text: 'common:messages.remove.succeeded' });
    }
  };

  const excludeUndefinedOptionIfCreate = (option) => option.value !== 'UNDEFINED' || !!model.uuid;
  const excludeUndefinedOptionIfExistingValueNotUndefined = (option) => option.value !== 'UNDEFINED' || model.useType === 'UNDEFINED';

  const voucherUseTypes = config.voucherUseTypes.options(t)
    .filter(excludeUndefinedOptionIfCreate)
    .filter(excludeUndefinedOptionIfExistingValueNotUndefined);

  return (
    <section ref={widthRef} style={ width > 0 ? {width} : null }>
      <Switch>
        <Route exact path={path}>
          <PageTitle t={t} />
          {renderIndicatorIfLoading()}
          <div className="yoso-table bg-white">
            <div className="row mb-2 table-header">
              <div className="col-10"></div>
              <div className="text-end">
              <input placeholder='Hae' value={search} onInput={(e) => { setSearch(e.target.value); }} />
                <a onClick={() => gotoEdit()} className="btn btn-primary">
                  {t('common:actions.create.new', { 'object': t('voucher.singular') })}
                </a>
              </div>
            </div>
            <div className="table-body">
              <VoucherTable t={t} data={currentData} onItemRemove={handleRemove} onItemEdit={gotoEdit} />
              <TablePagination currentPage={result.page + 1} totalPages={result.totalPageCount} onPageClick={setCurrentPage} />
            </div>
          </div>
        </Route>
        <Route exact path={`${path}/:key`}>
          <PageTitle t={t} titleKey={model.uuid ? 'edit.title' : 'create.title'} />
          <VoucherEditForm t={t} locale={i18n.language} model={model} discountTypes={discountTypes} voucherUseTypes={voucherUseTypes} tagSuqggestions={voucherTagSuggestions} onChange={handleChange} onSubmit={handleSubmit}>

            <div className="col-sm-6 col-xs-12 text-start">
              <button type="button" className="btn btn-danger" onClick={gotoList}>{t('common:actions.cancel')}</button>
            </div>

          </VoucherEditForm>
        </Route>
      </Switch>
    </section>
  );

};

export default withTranslation('voucherManagement')(VoucherManagement);
